import { useState, useEffect, useRef } from 'react';
import Button from '../../../components/base/Button';

declare global {
  interface Window {
    Calendly: any;
  }
}

export default function ServicesSection() {
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const calendlyRef = useRef<HTMLDivElement>(null);

  const mainService = {
    title: 'Individual Online Session',
    duration: '1 hour of personalized art therapy',
    price: '80 USD',
    calendlyUrl: 'https://calendly.com/yuvati-art/1-hour-online-session'
  };

  // Load Calendly script once
  useEffect(() => {
    if (window.Calendly) {
      setCalendlyLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      setCalendlyLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Calendly script');
      setCalendlyLoaded(false);
    };
    
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);

  // Load Stripe script
  useEffect(() => {
    const stripeScript = document.createElement('script');
    stripeScript.src = 'https://js.stripe.com/v3/buy-button.js';
    stripeScript.async = true;
    document.head.appendChild(stripeScript);

    return () => {
      const existingStripeScript = document.querySelector('script[src="https://js.stripe.com/v3/buy-button.js"]');
      if (existingStripeScript && existingStripeScript.parentNode) {
        existingStripeScript.parentNode.removeChild(existingStripeScript);
      }
    };
  }, []);

  // Initialize Calendly widget
  useEffect(() => {
    if (!calendlyLoaded || !window.Calendly || !calendlyRef.current) return;

    setWidgetLoading(true);
    
    // Clear existing widget
    calendlyRef.current.innerHTML = '';
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (calendlyRef.current && window.Calendly) {
        try {
          window.Calendly.initInlineWidget({
            url: mainService.calendlyUrl,
            parentElement: calendlyRef.current,
            prefill: {},
            utm: {},
            styles: {
              height: '900px'
            }
          });
          setWidgetLoading(false);
        } catch (error) {
          console.error('Error initializing Calendly widget:', error);
          setWidgetLoading(false);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [calendlyLoaded]);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openLegalModal = () => {
    setShowLegalModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLegalModal = () => {
    setShowLegalModal(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      {/* Legal Information Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1c1208]/50 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col my-4">
            {/* Modal Header */}
            <div className="px-4 sm:px-8 py-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 
                  className="text-xl sm:text-2xl font-bold cinematic-text-dark pr-4"
                >
                  Legal Information & Cancellation Policy
                </h3>
                <button
                  onClick={closeLegalModal}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                  aria-label="Close modal"
                >
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="px-4 sm:px-8 py-6 overflow-y-auto flex-grow">
              <div className="space-y-6 text-gray-700">
                {/* Disclaimer */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Disclaimer</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    Art therapy is a space for self-exploration, creativity, and emotional support.
                    It can be a meaningful part of your healing journey, but it is not a substitute for medical, psychiatric, or psychological treatment if such care is needed.
                    By booking a session, you acknowledge responsibility for your own emotional well-being and personal decisions made during or after the session.
                  </p>
                </div>

                {/* Liability */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Liability</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    The therapist is not responsible for any outcomes, emotional reactions, or decisions made based on the content of the session.
                    Sessions are designed to provide gentle guidance and self-reflection, not to diagnose or offer medical advice.
                  </p>
                </div>

                {/* Confidentiality */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Confidentiality</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    Everything you share during sessions is treated with care and confidentiality.
                    Your information will never be shared with third parties unless required by law or if there is a serious risk of harm to yourself or others.
                  </p>
                </div>

                {/* Cancellations & Rescheduling */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Cancellations & Rescheduling</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    I completely understand that life can be unpredictable.
                    If you need to reschedule or cancel, please let me know at least 24 hours in advance.
                    This allows me to offer the time to someone else who may be waiting.
                    Cancellations made less than 24 hours before the session or missed appointments are non-refundable.
                  </p>
                </div>

                {/* Late Arrival */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Late Arrival</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    If you arrive late, the session may still need to end at the originally scheduled time so that other clients can begin on time.
                  </p>
                </div>

                {/* Technical Issues */}
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Technical Issues (for online sessions)</h4>
                  <p className="leading-relaxed text-sm sm:text-base">
                    If there are any technical difficulties, please don't worry.
                    If the issue is on my side, the session will be rescheduled free of charge.
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-4 sm:px-8 py-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeLegalModal}
                className="w-full px-6 py-3 rounded-xl bg-[#2a1a0e] text-white font-medium hover:bg-[#1c1208] transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Services & Pricing Header */}
      <section id="services-pricing" className="py-20 cinematic-bg-light-alt overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl lg:text-5xl font-bold cinematic-text-dark mb-8 px-2"
            >
              Services & Pricing
            </h2>
            
            {/* Step-by-Step Instruction - Moved here */}
            <div className="flex items-center justify-center gap-3 flex-wrap mb-8">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  1
                </div>
                <span className="text-sm sm:text-base font-medium text-gray-700">Book your session</span>
              </div>
              <i className="ri-arrow-right-line text-gray-400 text-lg"></i>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  2
                </div>
                <span className="text-sm sm:text-base font-medium text-gray-700">Pay</span>
              </div>
            </div>
          </div>
          
          {/* Two Column Layout - Desktop / Stacked - Mobile */}
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start lg:items-stretch w-full">
            
            {/* Left Column / First Card - Calendly Widget */}
            <div id="calendly-widget" className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg border border-gray-100 flex flex-col w-full min-w-0 order-2 lg:order-1 h-full">
              
              {/* Session Description Block - Moved from right column */}
              <div className="mb-6 pb-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-2xl sm:text-3xl font-bold cinematic-text-dark mb-3 font-playfair break-words">
                  Schedule Your Individual Online* Session
                </h3>
                <div className="text-3xl sm:text-4xl font-bold text-amber-600 mb-4 font-playfair">
                  80 USD
                </div>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 break-words">
                  50-minute online art therapy session designed to support self-expression, emotional clarity, and gentle creative healing.
                </p>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 break-words">
                  * Individual and group offline sessions in Chiang Mai are available upon request. Please contact me for details.
                </p>
                <p className="text-xs sm:text-sm text-gray-500 break-words">
                  By booking a session, you agree to the{' '}
                  <button
                    onClick={openLegalModal}
                    className="text-amber-600 hover:text-amber-700 underline cursor-pointer font-medium"
                  >
                    Legal Information & Cancellation Policy
                  </button>
                  .
                </p>
              </div>
              
              <div className="relative w-full min-w-0 flex-shrink-0">
                <div 
                  ref={calendlyRef}
                  className="w-full transition-opacity duration-500" 
                  style={{ 
                    height: '900px',
                    opacity: (!calendlyLoaded || widgetLoading) ? 0 : 1
                  }}
                ></div>
                
                {(!calendlyLoaded || widgetLoading) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg" style={{ height: '900px' }}>
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto mb-4"></div>
                      <p className="text-sm cinematic-text-dark opacity-60">
                        Loading calendar...
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              <p className="text-xs cinematic-text-dark opacity-60 mt-4 text-center px-2 flex-shrink-0 break-words">
                Select your preferred date and time from the calendar above
              </p>
            </div>
            
            {/* Right Column / Second Card - Unified Services Container */}
            <div className="flex flex-col gap-6 w-full min-w-0 order-1 lg:order-2 h-full">
              
              {/* Unified White Background Block - Session Info & Next Steps */}
              <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg border border-gray-100">
                {/* About the Session */}
                <div className="mb-8">
                  <div className="mb-6">
                    <h3 className="text-2xl sm:text-3xl font-bold cinematic-text-dark mb-2 font-playfair">
                      About the Session
                    </h3>
                    <p className="text-base sm:text-lg text-amber-600 font-medium">
                      Transform through personalized art therapy
                    </p>
                  </div>
                  
                  <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">
                    Experience the power of art therapy from the comfort of your own space. Each session is tailored to your unique needs, providing a safe environment for self-expression and healing through creative exploration.
                  </p>
                  
                  <ul className="space-y-2 text-sm sm:text-base text-gray-700 leading-relaxed">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>No special materials are required.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>For your first session, a simple pen and a sheet of paper are enough.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>If you wish, you may prepare any art materials that feel interesting or emotionally engaging — they can enrich the creative process, but they are not required.</span>
                    </li>
                  </ul>
                </div>

                {/* Your Next Step */}
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold cinematic-text-dark mb-4 font-playfair">
                    Your Next Step
                  </h3>
                  
                  <ul className="space-y-2 text-sm sm:text-base text-gray-700 leading-relaxed">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Choose your session option below</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Complete the secure payment</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>You'll receive an email confirmation with all details</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* 2. Yellow Background Block - Individual session */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-3xl p-6 sm:p-8 shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-100 rounded-full opacity-20 transform translate-x-16 -translate-y-16"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-star-fill text-white text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800 font-playfair">Individual session</h3>
                      </div>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-amber-600 font-playfair">80 USD</div>
                  </div>

                  <a
                    href="https://buy.stripe.com/3cI4gz4wPcpx4Sa1TLd3i0e"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <button className="w-full px-8 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base">
                      Pay
                    </button>
                  </a>
                </div>
              </div>

              {/* 3. Cocoa Brown Background Block - Therapeutic process */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-3xl p-6 sm:p-8 shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-100 rounded-full opacity-20 transform translate-x-16 -translate-y-16"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#7B4F2E' }}>
                        <i className="ri-calendar-check-fill text-white text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800 font-playfair">Therapeutic process<br />(10 sessions)</h3>
                      </div>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold font-playfair" style={{ color: '#7B4F2E' }}>700 USD</div>
                  </div>

                  <p className="text-sm text-gray-500 mb-4 leading-relaxed">
                    Most therapeutic work unfolds over time. The multi-session format supports depth, continuity, and integration.
                  </p>

                  <a
                    href="https://buy.stripe.com/cNieVd9R9blt98qfKBd3i0d"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <button className="w-full px-8 py-3 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base" style={{ background: '#7B4F2E' }} onMouseEnter={(e) => e.currentTarget.style.background = '#5C3A22'} onMouseLeave={(e) => e.currentTarget.style.background = '#7B4F2E'}>
                      Pay
                    </button>
                  </a>
                </div>
              </div>

              {/* Need Help Block */}
              <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg border border-gray-100 lg:flex-1">
                <h3 className="text-2xl sm:text-3xl font-bold cinematic-text-dark mb-4 font-playfair">
                  Need Help?
                </h3>
                
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-6">
                  If you have any questions, feel free to contact me in any convenient way.
                </p>

                <button
                  onClick={scrollToContact}
                  className="w-full px-8 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  Contact me
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
